import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import '@d3fc/d3fc-element';
import { extentLinear } from '@d3fc/d3fc-extent';
import { seriesSvgMulti } from '@d3fc/d3fc-series';
import { postfixNumber } from '@instinet-analytics/core-lib';
import TimeSeriesChart from 'utils/components/TimeSeriesChart/TimeSeriesChart';
import gridlines from 'utils/components/TimeSeriesChart/gridlines';
import { useYScale } from 'utils/components/TimeSeriesChart/yAxis';
import { combineToggleableClassnames } from 'utils/toggleableClassnames/toggleableClassnames';
import {
    FillsGraphData,
    deriveBucketActualVolume,
    deriveBucketExpectedVolume,
} from 'widgets/fillsGraph/fillsGraphData';
import ExpectedVsActualChartLegend from './ExpectedVsActualChartLegend';
import './ExpectedVsActualChart.scss';
import { expectedVsActualSeries } from './expectedVsActualSeries';
import {
    ExpectedVsActualConfig,
    FillsChartType,
} from '../store/configuration/FillsGraphConfiguration';
import { useShowCollapsableCompanionChart } from 'widgets/fillsGraph/collapsableCompanionCharts/useShowCollapsableCompanionChart';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import { buildExpectedVsActualChartData } from 'widgets/fillsGraph/expectedVsActual/expectedVsActualChartData';
import classNames from 'classnames';
import { useFillsGraphComponentSlice } from 'widgets/fillsGraph/store/component/componentSlice';
import { deriveLastBinYAxisLabelProps } from 'widgets/fillsGraph/expectedVsActual/lastBinLabels';
import AppContext from 'app/AppContext';
import styleConstants from 'widgets/fillsGraph/variables.module.scss';
import YAxisLabel from 'widgets/fillsGraph/axisLabel/YAxisLabel';

const XAXIS_HEIGHT = parseInt(styleConstants.xAxisHeight);

const postFixNumberWithPercent = (n: number) => `${postfixNumber(n)}%`;

export interface ExpectedVsActualData {
    time: number;
    expectedVolume: number;
    actualVolume: number;
    barWidthPercent: number;
}

export interface ExpectedVsActualChartProps {
    height: number;
    chartHeight: number;
    width: number;
    fillsGraphContainerLeft: number;
    fillsGraphData: FillsGraphData;
    fillsGraphPortfolioData: FillsGraphPortfolioData;
    config: ExpectedVsActualConfig;
    bucketSizeInMs: number;
    displayXAxis: boolean;
}

export default function ExpectedVsActualChart(
    props: ExpectedVsActualChartProps
) {
    const {
        height,
        chartHeight,
        width,
        fillsGraphData,
        fillsGraphPortfolioData,
        displayXAxis,
        fillsGraphContainerLeft,
        config,
        bucketSizeInMs,
    } = props;

    const { numberFormat } = useContext(AppContext);

    const { data, volumesArePercentages } = fillsGraphData;
    const { side } = fillsGraphPortfolioData;
    const {
        displayActual,
        displayExpected,
        autoHideLastBucket,
        autoHideLastBucketThresholdPercent,
    } = config;
    const {
        component: { showAutoHiddenLastBucket },
        dispatchComponentChanged,
    } = useFillsGraphComponentSlice();

    const renderCompanionChart = useShowCollapsableCompanionChart(
        FillsChartType.ExpectedVsActual
    );

    const {
        bucketedData,
        chartData,
        chartMax,
        lastBucketExpectedVolume,
        lastBucketActualVolume,
        lastBucketAboveThreshold,
    } = buildExpectedVsActualChartData(
        data,
        bucketSizeInMs,
        autoHideLastBucket,
        autoHideLastBucketThresholdPercent,
        showAutoHiddenLastBucket
    );

    const plotAreaHeight = chartHeight - XAXIS_HEIGHT;

    const yDomain =
        data.length > 0 &&
        extentLinear()
            .accessors([
                deriveBucketActualVolume,
                deriveBucketExpectedVolume,
                () => 0,
            ])
            .pad([0, 0.1])(bucketedData);

    const yTicks = 3;
    const yScale = useYScale(yDomain, [0, 1]);

    const [series, setSeries] = useState();
    useEffect(() => {
        setSeries(() =>
            seriesSvgMulti().series([
                gridlines(yTicks),
                expectedVsActualSeries(bucketedData.length, width),
            ])
        );
    }, [yTicks, bucketedData.length, width]);

    const yAxisLabelProps = deriveLastBinYAxisLabelProps(
        chartMax,
        lastBucketExpectedVolume,
        lastBucketActualVolume,
        numberFormat,
        yScale,
        plotAreaHeight,
        width,
        volumesArePercentages
    );

    const toggleableClassnames = combineToggleableClassnames(
        { classname: 'ExpectedVsActualChart' },
        { classname: 'companion-chart' },
        { classname: 'hide-x-axis', hideClassname: displayXAxis },
        { classname: side === 'B' ? 'buy' : 'sell' },
        { classname: 'hide-actual', hideClassname: displayActual },
        { classname: 'hide-expected', hideClassname: displayExpected }
    );

    const cssClasses = classNames(
        toggleableClassnames,
        autoHideLastBucket && 'auto-hide-last-bucket'
    );

    const onLastBinLabelClicked = useCallback(() => {
        dispatchComponentChanged({
            showAutoHiddenLastBucket: !showAutoHiddenLastBucket,
        });
    }, [dispatchComponentChanged, showAutoHiddenLastBucket]);

    const displayLastBinLabels = autoHideLastBucket && lastBucketAboveThreshold;

    const yTickFormat = volumesArePercentages
        ? postFixNumberWithPercent
        : postfixNumber;

    const style = useMemo(() => ({ height }), [height]);

    return (
        <div className={cssClasses} style={style}>
            <ExpectedVsActualChartLegend
                fillsGraphData={fillsGraphData}
                fillsGraphPortfolioData={fillsGraphPortfolioData}
                fillsGraphContainerLeft={fillsGraphContainerLeft}
            />
            {renderCompanionChart && (
                <div className="chart-container">
                    {displayLastBinLabels && !!lastBucketActualVolume && (
                        <YAxisLabel
                            {...yAxisLabelProps.actual}
                            onClick={onLastBinLabelClicked}
                        />
                    )}
                    {displayLastBinLabels && !!lastBucketExpectedVolume && (
                        <YAxisLabel
                            {...yAxisLabelProps.expected}
                            onClick={onLastBinLabelClicked}
                        />
                    )}

                    <TimeSeriesChart
                        id="ExpectedVsActualChart"
                        height={chartHeight}
                        data={chartData}
                        series={series}
                        displayXAxis={displayXAxis}
                        yScale={yScale}
                        yTicks={yTicks}
                        yTickFormat={yTickFormat}
                    />
                </div>
            )}
        </div>
    );
}
